import { topCampaigns, topButtons, recentLeads, recentEvents, topSources } from '../../../mocks/dashboard';

const sourceColors: Record<string, string> = {
  'Google Ads': '#4285F4',
  'Instagram': '#E1306C',
  'Facebook': '#1877F2',
  'Organik': '#7CB342',
  'TikTok': '#69C9D0',
};

const eventColors: Record<string, string> = {
  form_submit: '#7CB342',
  whatsapp_click: '#25D366',
  page_view: '#64B5F6',
  button_click: '#D4AF37',
  form_start: '#FFB74D',
};

const eventIcons: Record<string, string> = {
  form_submit: 'ri-check-double-line',
  whatsapp_click: 'ri-whatsapp-line',
  page_view: 'ri-eye-line',
  button_click: 'ri-cursor-line',
  form_start: 'ri-file-edit-line',
};

export function TopCampaignsWidget() {
  return (
    <div className="bg-white rounded-xl2 shadow-card p-5">
      <h4 className="text-[14px] font-semibold text-charcoal-600 mb-4">En Çok İncelenen Kampanyalar</h4>
      <div className="space-y-3">
        {topCampaigns.map((c, i) => (
          <div key={c.id} className="flex items-center gap-3 group cursor-pointer">
            <span className="text-[12px] font-bold text-charcoal-200 w-4 flex-shrink-0">{i + 1}</span>
            <div className="w-10 h-10 rounded-xl overflow-hidden flex-shrink-0 bg-[#FAF8F4]">
              <img src={c.img} alt={c.title} className="w-full h-full object-cover object-top" />
            </div>
            <p className="text-[12px] text-charcoal-500 flex-1 leading-tight group-hover:text-champagne-600 transition-colors line-clamp-2">{c.title}</p>
            <span className="text-[11px] font-semibold text-champagne-600 bg-champagne-50 px-2 py-0.5 rounded-full flex-shrink-0 whitespace-nowrap">{c.views.toLocaleString('tr-TR')}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

export function TopButtonsWidget() {
  return (
    <div className="bg-white rounded-xl2 shadow-card p-5">
      <h4 className="text-[14px] font-semibold text-charcoal-600 mb-4">En Çok Tıklanan Butonlar</h4>
      <div className="space-y-3.5">
        {topButtons.map(b => (
          <div key={b.name}>
            <div className="flex items-center justify-between mb-1">
              <span className="text-[12px] text-charcoal-500 font-medium">{b.name}</span>
              <span className="text-[12px] font-semibold text-charcoal-600">{b.clicks.toLocaleString('tr-TR')}</span>
            </div>
            <div className="h-1.5 bg-[#F0EDE8] rounded-full overflow-hidden">
              <div
                className="h-full rounded-full transition-all duration-700"
                style={{ width: `${(b.clicks / b.max) * 100}%`, background: 'linear-gradient(90deg, #D4AF37, #E8C87C)' }}
              />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export function RecentLeadsWidget() {
  const sourceColorMap: Record<string, string> = {
    'Google Ads': 'bg-blue-50 text-blue-600',
    'Instagram': 'bg-pink-50 text-pink-600',
    'Facebook': 'bg-indigo-50 text-indigo-600',
    'Organik': 'bg-green-50 text-green-600',
  };
  return (
    <div className="bg-white rounded-xl2 shadow-card p-5">
      <div className="flex items-center justify-between mb-4">
        <h4 className="text-[14px] font-semibold text-charcoal-600">Son Leadler</h4>
        <span className="text-[11px] text-champagne-500 font-medium cursor-pointer hover:underline whitespace-nowrap">Tümünü Gör</span>
      </div>
      <div className="space-y-3">
        {recentLeads.map(l => (
          <div key={l.id} className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-full overflow-hidden flex-shrink-0 bg-[#FAF8F4]">
              <img src={l.avatar} alt={l.name} className="w-full h-full object-cover object-top" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-[12px] font-semibold text-charcoal-600 truncate">{l.name}</p>
              <p className="text-[11px] text-charcoal-300 truncate">{l.campaign}</p>
            </div>
            <div className="flex flex-col items-end gap-1">
              <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded-full whitespace-nowrap ${sourceColorMap[l.source] || 'bg-gray-50 text-gray-600'}`}>{l.source}</span>
              <span className="text-[10px] text-charcoal-300 whitespace-nowrap">{l.time}</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export function RecentEventsWidget() {
  return (
    <div className="bg-white rounded-xl2 shadow-card p-5">
      <div className="flex items-center justify-between mb-4">
        <h4 className="text-[14px] font-semibold text-charcoal-600">Son Event Kayıtları</h4>
        <span className="text-[11px] text-champagne-500 font-medium cursor-pointer hover:underline whitespace-nowrap">Tümünü Gör</span>
      </div>
      <div className="relative">
        <div className="absolute left-3 top-0 bottom-0 w-px bg-[#F0EDE8]" />
        <div className="space-y-3 pl-8">
          {recentEvents.map(e => (
            <div key={e.id} className="relative">
              <div
                className="absolute -left-5 top-1.5 w-4 h-4 rounded-full flex items-center justify-center"
                style={{ backgroundColor: `${e.color}20` }}
              >
                <div className="w-2 h-2 flex items-center justify-center">
                  <i className={`${eventIcons[e.type]} text-[9px]`} style={{ color: e.color }} />
                </div>
              </div>
              <p className="text-[12px] font-medium text-charcoal-500">{e.label}</p>
              <p className="text-[11px] text-charcoal-300">{e.desc}</p>
              <p className="text-[10px] text-charcoal-200 mt-0.5">{e.time}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export function TopSourcesWidget() {
  return (
    <div className="bg-white rounded-xl2 shadow-card p-5">
      <h4 className="text-[14px] font-semibold text-charcoal-600 mb-4">En İyi Kaynaklar</h4>
      <div className="space-y-3">
        {topSources.map(s => (
          <div key={s.source} className="flex items-center gap-3">
            <div
              className="w-8 h-8 rounded-xl flex items-center justify-center flex-shrink-0"
              style={{ backgroundColor: `${sourceColors[s.source]}15` }}
            >
              <div className="w-4 h-4 flex items-center justify-center">
                <i className={`${s.icon} text-[14px]`} style={{ color: sourceColors[s.source] }} />
              </div>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-[12px] font-semibold text-charcoal-600">{s.source}</p>
              <p className="text-[11px] text-charcoal-300">{s.visitors.toLocaleString('tr-TR')} ziyaretçi</p>
            </div>
            <span
              className="text-[12px] font-bold px-2 py-0.5 rounded-full whitespace-nowrap"
              style={{ color: s.conversion >= 6 ? '#7CB342' : s.conversion >= 4 ? '#D4AF37' : '#E57373', backgroundColor: s.conversion >= 6 ? '#F0FFF0' : s.conversion >= 4 ? '#FFFBEB' : '#FFF5F5' }}
            >
              {s.conversion}%
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}
